import type {
  ColorScale,
  ColorScheme,
  ColorSchemeColors,
  PilotThemeConfig,
} from "./theme-config.js";

function hexToRgbTriplet(hex: string): string | null {
  const cleaned = hex.replace("#", "").trim();
  let r: number, g: number, b: number;
  if (cleaned.length === 3) {
    r = parseInt(cleaned[0] + cleaned[0], 16);
    g = parseInt(cleaned[1] + cleaned[1], 16);
    b = parseInt(cleaned[2] + cleaned[2], 16);
  } else if (cleaned.length === 6 || cleaned.length === 8) {
    r = parseInt(cleaned.slice(0, 2), 16);
    g = parseInt(cleaned.slice(2, 4), 16);
    b = parseInt(cleaned.slice(4, 6), 16);
  } else {
    return null;
  }
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
  return `${r} ${g} ${b}`;
}

function emitScale(lines: string[], name: string, scale: ColorScale): void {
  for (const [shade, value] of Object.entries(scale)) {
    if (!value) continue;
    lines.push(`  --color-${name}-${shade}: ${value};`);
    const rgb = hexToRgbTriplet(value);
    if (rgb) lines.push(`  --color-${name}-${shade}-rgb: ${rgb};`);
  }
}

function emitSchemeColors(lines: string[], colors: ColorSchemeColors): void {
  emitScale(lines, "primary", colors.primary);
  emitScale(lines, "accent", colors.accent);
  if (colors.secondary) emitScale(lines, "secondary", colors.secondary);
  if (colors.success) emitScale(lines, "success", colors.success);
  if (colors.error) emitScale(lines, "error", colors.error);
  if (colors.warning) emitScale(lines, "warning", colors.warning);
  if (colors.info) emitScale(lines, "info", colors.info);

  lines.push(`  --background: ${colors.background.primary};`);
  lines.push(`  --background-primary: ${colors.background.primary};`);
  if (colors.background.secondary)
    lines.push(`  --background-secondary: ${colors.background.secondary};`);
  if (colors.background.tertiary)
    lines.push(`  --background-tertiary: ${colors.background.tertiary};`);

  if (colors.foreground) {
    lines.push(`  --foreground: ${colors.foreground.primary};`);
    lines.push(`  --foreground-primary: ${colors.foreground.primary};`);
    if (colors.foreground.secondary)
      lines.push(`  --foreground-secondary: ${colors.foreground.secondary};`);
    if (colors.foreground.muted)
      lines.push(`  --foreground-muted: ${colors.foreground.muted};`);
  }

  if (colors.surface) {
    lines.push(`  --surface: ${colors.surface.primary};`);
    lines.push(`  --surface-primary: ${colors.surface.primary};`);
    if (colors.surface.elevated)
      lines.push(`  --surface-elevated: ${colors.surface.elevated};`);
    if (colors.surface.overlay)
      lines.push(`  --surface-overlay: ${colors.surface.overlay};`);
  }

  lines.push(`  --primary: ${colors.primary["500"]};`);
  lines.push(`  --accent: ${colors.accent["500"]};`);
  if (colors.secondary) lines.push(`  --secondary: ${colors.secondary["500"]};`);
  if (colors.success) lines.push(`  --success: ${colors.success["500"]};`);
  if (colors.error) {
    lines.push(`  --error: ${colors.error["500"]};`);
    lines.push(`  --destructive: ${colors.error["500"]};`);
  }
  if (colors.warning) lines.push(`  --warning: ${colors.warning["500"]};`);
  if (colors.info) lines.push(`  --info: ${colors.info["500"]};`);
}

export function generateThemeCSS(
  theme: PilotThemeConfig,
  scheme: ColorScheme = "dark",
): string {
  const colors = scheme === "dark" ? theme.colors.dark : (theme.colors.light ?? theme.colors.dark);
  const lines: string[] = [];
  emitSchemeColors(lines, colors);
  lines.push(`  --theme-name: "${theme.name}";`);
  return lines.join("\n");
}

export function generateBaseCSS(theme: PilotThemeConfig): string {
  const lines: string[] = [];

  if (theme.spacing) {
    lines.push(`  --grid-unit: ${theme.spacing.gridUnit}px;`);
    if (theme.spacing.scale) {
      for (const [key, value] of Object.entries(theme.spacing.scale)) {
        lines.push(`  --space-${key}: ${value};`);
      }
    }
  }

  if (theme.typography) {
    const fmt = (stack: string[]) =>
      stack.map((f) => (f.includes(" ") ? `"${f}"` : f)).join(", ");
    lines.push(`  --font-display: ${fmt(theme.typography.fontFamily.display)};`);
    lines.push(`  --font-body: ${fmt(theme.typography.fontFamily.body)};`);
    if (theme.typography.fontFamily.mono)
      lines.push(`  --font-mono: ${fmt(theme.typography.fontFamily.mono)};`);
    if (theme.typography.fontSizes) {
      for (const [key, value] of Object.entries(theme.typography.fontSizes)) {
        if (value) lines.push(`  --text-${key}: ${value};`);
      }
    }
  }

  if (theme.motion) {
    lines.push(`  --timing-function: ${theme.motion.timingFunction};`);
    if (theme.motion.duration) {
      lines.push(`  --duration-fast: ${theme.motion.duration.fast};`);
      lines.push(`  --duration-normal: ${theme.motion.duration.normal};`);
      lines.push(`  --duration-slow: ${theme.motion.duration.slow};`);
    }
  }

  return lines.join("\n");
}

export function generateFullStylesheet(themes: PilotThemeConfig[]): string {
  if (themes.length === 0) {
    throw new Error("generateFullStylesheet: at least one theme is required");
  }
  const base = generateBaseCSS(themes[0]);
  const blocks = themes.map(
    (t) => `[data-theme="${t.name}"] {\n${generateThemeCSS(t, "dark")}\n}`,
  );
  const lightBlocks = themes
    .filter((t) => t.colors.light)
    .map(
      (t) =>
        `[data-theme="${t.name}"][data-color-scheme="light"] {\n${generateThemeCSS(t, "light")}\n}`,
    );
  return `/* Auto-generated by web/src/theme/css-generator.ts */\n:root {\n${base}\n}\n\n${blocks.join("\n\n")}${lightBlocks.length ? "\n\n" + lightBlocks.join("\n\n") : ""}\n`;
}

export function generateCSSVariables(
  theme: PilotThemeConfig,
  scheme: ColorScheme = "dark",
): Record<string, string> {
  const css = generateThemeCSS(theme, scheme);
  const vars: Record<string, string> = {};
  for (const line of css.split("\n")) {
    const match = line.match(/^\s*(--[\w-]+):\s*(.+);$/);
    if (match) vars[match[1]] = match[2];
  }
  return vars;
}
